import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const BrainGames = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'braingames'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container brain-games-page">
      <h1 className="page-title">Brain Games and Puzzles</h1>

      {/* Include the image */}
      <img
        src="/images/wf-braingames.webp"
        alt="Brain Games"
        className="page-image"
      />

      <p className="page-intro">
        Welcome to the Brain Games and Puzzles section of Wholeness Forward. Engaging in brain games and puzzles is not only fun but also an excellent way to keep your mind sharp and healthy.
      </p>

      <p className="page-text">
        In this section, we explore the benefits of brain games, recommend popular games, and provide tips on incorporating them into your routine.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Benefits of Brain Games</h2>
      <ul className="content-list">
        <li><strong>Improves Memory:</strong> Games that challenge your recall help enhance both short- and long-term memory.</li>
        <li><strong>Boosts Problem-Solving Skills:</strong> Logic puzzles and strategy games require critical thinking and adaptability.</li>
        <li><strong>Increases Mental Agility:</strong> Quick decision-making games train your brain to think on its feet.</li>
        <li><strong>Promotes Relaxation:</strong> Focusing on enjoyable tasks reduces stress and improves mental well-being.</li>
        <li><strong>Prevents Cognitive Decline:</strong> Regularly engaging in challenging mental activities may help delay dementia.</li>
      </ul>

      <hr className="page-divider" />
      
      <h2 className="page-heading">Curated Video Collection</h2>
      <p className="page-text">Explore these videos to learn about the cognitive benefits of brain games:</p>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}


      <hr className="page-divider" />

      <h3 className="subheading">Join the Brain Games Community</h3>
      <p className="page-text">
        Share your favorite games, solve puzzles with others, or discuss strategies in our community forum.
      </p>
      <a href="/forum/Brain_Games" className="btn">Join the Discussion</a>
    </div>
  );
};

export default BrainGames;
